// Formatação pt-BR num lugar só — o app inteiro mostra dinheiro e data igual.

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const numero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 });
const dataHora = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const dataCurta = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const soHora = new Intl.DateTimeFormat('pt-BR', { timeStyle: 'short' });

export const brl = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : moeda.format(v);

export const qtd = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : numero.format(v);

export const dataHoraBR = (iso: string | null | undefined): string =>
  iso ? dataHora.format(new Date(iso)) : '—';

export const dataBR = (iso: string | null | undefined): string =>
  iso ? dataCurta.format(new Date(iso)) : '—';

/** Uma data PURA ('2026-08-25') em pt-BR, sem passar por Date.
 *
 *  `new Date('2026-08-25')` é meia-noite UTC — que no fuso da loja é o dia
 *  ANTERIOR, e o papel sairia com 24/08 num acerto feito dia 25. Colunas
 *  `date` do Postgres chegam exatamente nesse formato; aqui se fatia a string,
 *  que é a única leitura que não escorrega. */
export const dataSoDiaBR = (iso: string): string =>
  iso.slice(0, 10).split('-').reverse().join('/');

/**
 * O INSTANTE EXATO no relógio da loja: "28/10/2026 às 14:32:07".
 *
 * Pedido do dono (28/10/2026): a encomenda precisa dizer a hora em que foi
 * feita, para achar o atendimento na gravação das câmeras. Por isso o fuso é
 * FIXO em São Paulo, e não o do aparelho — celular com fuso trocado não pode
 * mandar alguém procurar na hora errada — e os segundos aparecem: na câmera,
 * um minuto é muito vídeo.
 */
const relogioDaLoja = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

export const dataHoraDaLoja = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const p = Object.fromEntries(
    relogioDaLoja.formatToParts(new Date(iso)).map((x) => [x.type, x.value]),
  );
  return `${p.day}/${p.month}/${p.year} às ${p.hour}:${p.minute}:${p.second}`;
};

/** Só a hora ("14:32") — para lista do MESMO dia, onde repetir a data é ruído. */
export const horaBR = (iso: string | null | undefined): string =>
  iso ? soHora.format(new Date(iso)) : '—';

/** 'YYYY-MM-DD' do fuso local — o dia do caixa é o dia da loja, não o UTC. */
export const hojeISO = (d = new Date()): string => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

export const mesISO = (d = new Date()): string => hojeISO(d).slice(0, 7);

/**
 * Início/fim de um dia local, em ISO com fuso — usado nos filtros de caixa.
 * `de`/`ate` são horas inteiras (0-23); `ate` é exclusivo no fim do intervalo.
 */
export const intervaloDoDia = (diaISO: string, horaDe = 0, horaAte = 24) => {
  const inicio = new Date(`${diaISO}T00:00:00`);
  inicio.setHours(horaDe, 0, 0, 0);
  const fim = new Date(`${diaISO}T00:00:00`);
  fim.setHours(horaAte, 0, 0, 0);
  return { inicio: inicio.toISOString(), fim: fim.toISOString() };
};

/**
 * Aceita "1.234,56", "1.500", "10.5", "1234" e devolve number (null se inválido).
 *
 * O ponto é ambíguo em pt-BR e essa ambiguidade já custou caro: `Number('1.500')`
 * é 1.5, então digitar R$ 1.500,00 gravava R$ 1,50 — erro de 1000x, silencioso,
 * direto no caixa. Regra adotada:
 *   * tem vírgula  → vírgula é decimal, ponto é milhar   ("1.234,56" → 1234.56)
 *   * só pontos, todos os grupos com 3 dígitos → milhar  ("1.500"    → 1500)
 *   * só pontos, qualquer outro tamanho → decimal        ("10.5"     → 10.5)
 *
 * O caso "1.234" querendo dizer 1,234 litros resolve para 1234 — quem digita
 * fração em pt-BR usa vírgula ("1,234"), e todo campo de dinheiro do app é
 * rotulado em R$.
 */
export const paraNumero = (texto: string): number | null => {
  const limpo = texto.trim().replace(/\s/g, '');
  if (!limpo) return null;

  let normalizado: string;
  if (limpo.includes(',')) {
    normalizado = limpo.replace(/\./g, '').replace(',', '.');
  } else {
    const grupos = limpo.split('.');
    normalizado = grupos.length > 1 && grupos.slice(1).every((g) => g.length === 3)
      ? grupos.join('')
      : limpo;
  }

  // Number() é permissivo demais: aceita '', '0x10', 'Infinity' e espaços.
  // Só passa o que é estritamente numérico.
  if (!/^-?\d*\.?\d+$/.test(normalizado)) return null;

  const n = Number(normalizado);
  return Number.isFinite(n) ? n : null;
};

/** Mesma normalização do norm_code() do banco — só [A-Z0-9]. */
export const normCode = (v: string): string => v.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Balcão sugerido no cadastro: custo × margem, pronto para o campo de preço
 * (string pt-BR, vírgula). Null quando ainda não dá para sugerir — campo vazio,
 * texto inválido, custo ou margem não positivos.
 *
 * A margem aqui é FERRAMENTA DE DIGITAÇÃO, não dado: o banco nunca a recebe
 * (regra 4 do CLAUDE.md — número derivado não se armazena; margem real é
 * balcão÷custo, calculada pela RPC margens sobre os dois preços gravados).
 */
export const precoSugerido = (custo: string, margem: string): string | null => {
  const c = paraNumero(custo);
  const m = paraNumero(margem);
  if (c === null || m === null || c <= 0 || m <= 0) return null;
  return (Math.round(c * m * 100) / 100).toFixed(2).replace('.', ',');
};
